import { Fingerprint, ShieldAlert } from 'lucide-react'
import { useEffect, useState } from 'react'

import type { LedgerAuditEntry } from '@/api/ledger-types'
import { ErrorSurface } from '@/components/error-surface'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { formatDateTime } from '@/lib/datetime'

import {
  AUDIT_OUTCOME_TONE,
  auditOutcomeLabel,
  auditSourceLabel,
} from './constants'
import { LEDGER_PERMISSIONS } from './permissions'
import { useLedgerAuditTrail } from './queries'

const PAGE_SIZE = 25

export interface LedgerAuditTrailProps {
  entityType: string
  entityId: string
  /** Whether this user holds `ops.audit.view`. The trail is not gated on a ledger permission. */
  canRead: boolean
  /** What an empty result means for this particular subject, in one line. */
  emptyHint: string
}

/**
 * The audit trail for one ledger entity.
 *
 * What this carries that no other read does is the *human* half of a posting: which named user
 * acted, whether anybody countersigned, and whether an attempt was refused. Every journal the
 * posting service writes is recorded under `entityType: JournalEntry` and
 * `entityId: <entry number>`, and a reversal is recorded against the original entry number as well.
 *
 * It is no longer the ledger's only read of what it has posted — `GET /ledger/journals/{id}` serves
 * an entry with every line on it, and the journal screen shows them. Read the two together: the
 * journal is what moved, and the trail is who moved it.
 *
 * Three limits are stated on screen rather than discovered:
 *
 * - it cannot be browsed — an entity type and an identifier have to be known in advance;
 * - it is gated on `ops.audit.view`, not on `ledger.journal.view`, so the permission that lets
 *   somebody read journals is not the permission that lets them read this;
 * - the handler scopes rows to the caller's accessible branches unless they hold
 *   `admin.access.all_branches`, so a branch-A reader cannot evidence a branch-B posting at all.
 *
 * Reading a trail is itself an audited action, so the query never fires speculatively.
 */
export function LedgerAuditTrail({ entityType, entityId, canRead, emptyHint }: LedgerAuditTrailProps) {
  const [page, setPage] = useState(1)

  // A new subject starts at its own first page rather than at whatever page the last one was on.
  useEffect(() => {
    setPage(1)
  }, [entityType, entityId])

  const query = useLedgerAuditTrail(
    entityId === '' ? null : { entityType, entityId, page, pageSize: PAGE_SIZE },
    canRead,
  )

  if (!canRead) {
    return (
      <div className="flex flex-col items-center gap-2 rounded-card border border-border-subtle bg-surface px-4 py-10 text-center">
        <ShieldAlert className="size-5 text-warning-text" aria-hidden />
        <p className="text-sm font-medium text-heading">The audit trail is restricted</p>
        <p className="max-w-md text-xs text-muted">
          Reading it needs <span className="font-mono">{LEDGER_PERMISSIONS.auditView}</span>, which is
          an operations permission rather than a ledger one. Holding{' '}
          <span className="font-mono">{LEDGER_PERMISSIONS.journalView}</span> is not enough — that is
          a property of the contract, not of this screen.
        </p>
      </div>
    )
  }

  if (entityId === '') {
    return <p className="py-8 text-center text-sm text-muted">{emptyHint}</p>
  }

  if (query.isPending) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 3 }, (_, index) => (
          <Skeleton key={index} className="h-24 w-full" />
        ))}
      </div>
    )
  }

  if (query.isError) {
    return (
      <ErrorSurface
        error={query.error}
        onRetry={() => void query.refetch()}
        title="The audit trail could not be read"
      />
    )
  }

  const totalPages = Math.max(1, Math.ceil(query.data.totalCount / Math.max(query.data.pageSize, 1)))

  if (query.data.items.length === 0) {
    return (
      <div className="rounded-card border border-border-subtle bg-surface px-4 py-10 text-center">
        <p className="text-sm font-medium text-heading">Nothing recorded under that identifier</p>
        <p className="mx-auto mt-1 max-w-lg text-xs text-muted">
          {emptyHint} An empty answer is also what a reader outside the branch sees: the trail is
          scoped to the branches you can reach unless you hold{' '}
          <span className="font-mono">{LEDGER_PERMISSIONS.allBranches}</span>.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <ol className="space-y-2">
        {query.data.items.map((entry) => (
          <AuditEntryCard key={entry.id} entry={entry} />
        ))}
      </ol>

      <div className="flex flex-wrap items-center gap-2">
        <span className="mr-auto text-xs text-muted">
          <span className="numeric tabular-nums">{query.data.totalCount}</span> entr
          {query.data.totalCount === 1 ? 'y' : 'ies'} · page{' '}
          <span className="numeric tabular-nums">{query.data.page}</span> of{' '}
          <span className="numeric tabular-nums">{totalPages}</span>
        </span>
        <Button
          variant="secondary"
          size="sm"
          disabled={page <= 1}
          onClick={() => setPage((current) => Math.max(1, current - 1))}
        >
          Previous
        </Button>
        <Button
          variant="secondary"
          size="sm"
          disabled={page >= totalPages}
          onClick={() => setPage((current) => current + 1)}
        >
          Next
        </Button>
      </div>
    </div>
  )
}

function AuditEntryCard({ entry }: { entry: LedgerAuditEntry }) {
  const hasState = entry.beforeJson !== null || entry.afterJson !== null

  return (
    <li className="rounded-card border border-border-subtle bg-surface-muted p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="flex items-center gap-2 font-mono text-xs font-medium text-heading">
          <Fingerprint className="size-3.5 text-brand-text" aria-hidden />
          {entry.actionCode}
        </span>
        <Badge tone={AUDIT_OUTCOME_TONE[entry.outcome] ?? 'neutral'}>
          {auditOutcomeLabel(entry.outcome)}
        </Badge>
      </div>

      <p className="mt-1 text-xs text-body">
        {entry.actorUsername}
        {/*
          Null and absent are different facts and are rendered as such. A countersigner is null on
          an action that was never countersigned — which is every action this module can perform —
          rather than missing because the caller may not see it.
        */}
        {entry.approverUsername === null ? '' : ` · countersigned by ${entry.approverUsername}`} ·{' '}
        <span className="numeric tabular-nums">{formatDateTime(entry.occurredAtUtc)}</span>
      </p>

      <p className="mt-0.5 text-2xs text-muted">
        {auditSourceLabel(entry.source)}
        {entry.branchId === null ? ' · institution-wide' : ` · branch ${entry.branchId}`}
        {entry.entityId === null ? '' : ` · ${entry.entityType} ${entry.entityId}`}
      </p>

      {entry.failureReason === null ? null : (
        <p className="mt-1 text-xs text-danger-text">{entry.failureReason}</p>
      )}

      {hasState ? (
        <details className="mt-2">
          <summary className="cursor-pointer text-2xs font-medium text-brand-text max-lg:min-h-11">
            Before and after
          </summary>
          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            <JsonPanel label="Before" json={entry.beforeJson} />
            <JsonPanel label="After" json={entry.afterJson} />
          </div>
        </details>
      ) : null}

      <p className="mt-2 font-mono text-2xs text-muted">ref {entry.correlationId}</p>
    </li>
  )
}

function JsonPanel({ label, json }: { label: string; json: string | null }) {
  return (
    <div className="min-w-0">
      <p className="text-2xs font-semibold tracking-wide text-muted uppercase">{label}</p>
      <pre className="mt-1 max-h-56 overflow-auto rounded-control border border-border-subtle bg-surface p-2 font-mono text-2xs text-body">
        {json === null ? '—' : prettyJson(json)}
      </pre>
    </div>
  )
}

/** Server-supplied JSON is shown as it arrived when it will not parse — never silently blanked. */
function prettyJson(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw) as unknown, null, 2)
  } catch {
    return raw
  }
}
